import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useContext, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import ContentPage from "@/components/contentPage";
import DeadLettersPanel, {
  AUTOMATIC_TIERING_ACTION,
} from "@/components/storageOps/DeadLettersPanel";
import PauseTogglePanel from "@/components/storageOps/PauseTogglePanel";
import StatusSummaryPanel from "@/components/storageOps/StatusSummaryPanel";
import TierChangeRequestPanel from "@/components/storageOps/TierChangeRequestPanel";
import TierDistributionPanel from "@/components/storageOps/TierDistributionPanel";
import TieringJobsPanel from "@/components/storageOps/TieringJobsPanel";
import {
  storageOpsSearchSchema,
  stripStorageOpsSearchDefaults,
  type StorageOpsSearch,
} from "@/components/storageOps/storageOps.utils";
import UserContext from "@/contexts/user";
import type { ChangeStorageTierParams } from "@/apiClient/models/ChangeStorageTierParams";
import type { TieringJobResponse } from "@/apiClient/models/TieringJobResponse";

export const Route = createFileRoute("/admin/storage-ops/")({
  component: StorageOpsAdminPage,
  validateSearch: storageOpsSearchSchema,
});

const POLL_INTERVAL_MS = 15_000;
const DISTRIBUTION_POLL_INTERVAL_MS = 60_000;

function UnauthorizedState() {
  return (
    <div className="mx-7 rounded-xl border border-amber-100 bg-amber-50 p-6 text-amber-700">
      You need superuser permissions to view storage operations.
    </div>
  );
}

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

function StorageOpsAdminPage() {
  const { api } = Route.useRouteContext();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const { user } = useContext(UserContext);
  const queryClient = useQueryClient();

  const updateSearch = useCallback(
    (update: Partial<StorageOpsSearch>) => {
      void navigate({
        replace: true,
        search: ((prev: StorageOpsSearch) =>
          stripStorageOpsSearchDefaults({ ...prev, ...update })) as never,
      });
    },
    [navigate]
  );
  const [pendingJobAction, setPendingJobAction] = useState<string | null>(null);

  const distributionQuery = useQuery({
    queryKey: ["storageOps", "distribution"],
    queryFn: () => api.storageOps.getTierDistribution(),
    enabled: user?.is_superuser === true,
    // Heavier aggregate than the job/status queries, so poll at a slower
    // cadence rather than every 15s.
    refetchInterval: DISTRIBUTION_POLL_INTERVAL_MS,
  });

  const jobsQuery = useQuery({
    queryKey: ["storageOps", "jobs"],
    queryFn: () => api.storageOps.listTieringJobs({}),
    enabled: user?.is_superuser === true,
    refetchInterval: POLL_INTERVAL_MS,
  });

  const statusSummaryQuery = useQuery({
    queryKey: ["storageOps", "statusSummary"],
    queryFn: () => api.storageOps.getTierStatusSummary(),
    enabled: user?.is_superuser === true,
    refetchInterval: POLL_INTERVAL_MS,
  });

  const deadLettersQuery = useQuery({
    queryKey: ["storageOps", "deadLetters"],
    queryFn: () => api.storageOps.listDeadLetters(),
    enabled: user?.is_superuser === true,
    refetchInterval: POLL_INTERVAL_MS,
  });

  const pauseQuery = useQuery({
    queryKey: ["storageOps", "pause"],
    queryFn: () => api.storageOps.getPauseState(),
    enabled: user?.is_superuser === true,
  });

  const refreshJobViews = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["storageOps", "jobs"] }),
      queryClient.invalidateQueries({
        queryKey: ["storageOps", "statusSummary"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["storageOps", "deadLetters"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["storageOps", "distribution"],
      }),
    ]);

  const handleSubmitTierChange = async (body: ChangeStorageTierParams) => {
    const job = await api.storageOps.requestStorageTierChange({
      requestBody: body,
    });
    await refreshJobViews();
    return job;
  };

  const runJobAction = async (
    jobPublicId: string,
    action: () => Promise<unknown>,
    successMessage: string
  ) => {
    setPendingJobAction(jobPublicId);
    try {
      await action();
      toast.success(successMessage);
      await refreshJobViews();
    } catch (error) {
      toast.error(`Action failed: ${errorMessage(error)}`);
    } finally {
      setPendingJobAction(null);
    }
  };

  const handleCancelJob = (jobPublicId: string) =>
    runJobAction(
      jobPublicId,
      () => api.storageOps.cancelPendingTieringRows({ jobPublicId }),
      "Cancellation requested. Unclaimed rows were cancelled; in-flight rows may finish."
    );

  const handleRerunWithFeesAllowed = (job: TieringJobResponse) => {
    const params = job.params as ChangeStorageTierParams | null;
    if (!params?.search || !params.target_tier) {
      toast.error("Job params are missing search criteria; cannot re-run.");
      return;
    }
    void runJobAction(
      job.public_id ?? "",
      () =>
        api.storageOps.requestStorageTierChange({
          requestBody: { ...params, allow_early_deletion_fees: true },
        }),
      "Re-submitted with early-deletion fees allowed."
    );
  };

  const handleRetierCancelledRows = (jobPublicId: string) =>
    runJobAction(
      jobPublicId,
      () => api.storageOps.retierCancelledRows({ jobPublicId }),
      "Cancelled rows re-requested at their marked tier."
    );

  const handleRetryFailed = (jobPublicId: string) =>
    runJobAction(
      jobPublicId,
      () => api.storageOps.retryFailedRows({ jobPublicId }),
      "Failed rows flipped back to requested."
    );

  const handleRetryAutomatic = () =>
    runJobAction(
      AUTOMATIC_TIERING_ACTION,
      () => api.storageOps.retryAutomaticFailedRows(),
      "Failed automatic-tiering rows flipped back to requested."
    );

  const handleSetPaused = async (paused: boolean) => {
    try {
      await api.storageOps.setPauseState({ requestBody: { paused } });
      toast.success(paused ? "Tiering paused." : "Tiering resumed.");
      await queryClient.invalidateQueries({
        queryKey: ["storageOps", "pause"],
      });
    } catch (error) {
      toast.error(`Failed to update pause state: ${errorMessage(error)}`);
    }
  };

  if (user && user.is_superuser !== true) {
    return (
      <ContentPage title={"Storage Ops"}>
        <UnauthorizedState />
      </ContentPage>
    );
  }

  return (
    <ContentPage title={"Storage Ops"}>
      <div className="mx-7 flex flex-col gap-5 pb-10">
        <PauseTogglePanel
          paused={pauseQuery.data?.paused === true}
          isLoading={pauseQuery.isLoading}
          onSetPaused={handleSetPaused}
        />

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <TierDistributionPanel
            rows={distributionQuery.data ?? []}
            isLoading={distributionQuery.isLoading}
            groupByManifestType={search.groupByManifestType}
            onGroupByManifestTypeChange={(groupByManifestType) =>
              updateSearch({ groupByManifestType })
            }
          />
          <StatusSummaryPanel
            rows={statusSummaryQuery.data ?? []}
            isLoading={statusSummaryQuery.isLoading}
          />
        </div>

        <TierChangeRequestPanel
          search={search}
          updateSearch={updateSearch}
          onPreview={(body) =>
            api.storageOps.previewStorageTierChangeRequest({
              requestBody: body,
            })
          }
          onSubmit={handleSubmitTierChange}
        />

        <TieringJobsPanel
          jobs={jobsQuery.data ?? []}
          isLoading={jobsQuery.isLoading}
          onCancelJob={handleCancelJob}
          onRerunWithFeesAllowed={handleRerunWithFeesAllowed}
          onRetierCancelledRows={handleRetierCancelledRows}
          pendingJobAction={pendingJobAction}
        />

        <DeadLettersPanel
          deadLetters={deadLettersQuery.data ?? []}
          jobs={jobsQuery.data ?? []}
          isLoading={deadLettersQuery.isLoading}
          onRetryFailed={handleRetryFailed}
          onRetryAutomatic={handleRetryAutomatic}
          pendingJobAction={pendingJobAction}
        />
      </div>
    </ContentPage>
  );
}
